import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Collapse,
  CollapseProps,
  Descriptions,
  Divider,
  Flex,
  Grid,
  Skeleton,
  Spin,
  Tabs,
  Typography
} from 'antd';
import Parser from 'html-react-parser';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getTicket } from 'src/apis/helpdesk/ticket.api';
import { getAdvancePaymentTicket } from 'src/apis/hr_ticket/advancePaymentTicket.api';
import { getBusinessTripTicket } from 'src/apis/hr_ticket/businessTripTicket.api';
import { BtnHrHandle } from 'src/components/v2/Button';
import { AppContext } from 'src/contexts/app.context';
import { HrTicketDetailContext } from 'src/contexts/hrTicketDetail.context';
import { TicketContext } from 'src/contexts/ticket.context';
import { IAdvancePaymentTicketDetail } from 'src/types/hr_ticket/advancePaymentTicket.type';
import { formatDate, formatToVND } from 'src/utils/utils';
import './AdvancePaymentTicketDetail.scss';
import { HRTicketStatusTag, TicketStatusTag } from 'src/components/v2/Tag/TicketStatusTag/TicketStatusTag';

const { Title, Text } = Typography;

type ActiveType = '1' | '2';

const { useBreakpoint } = Grid;

export default function AdvancePaymentTicketDetail() {
  const { setInvalidKey } = useContext(TicketContext);
  const [activeKey, setActiveKey] = useState<ActiveType>('1');
  const { setAdvancePaymentTicket, setLoading } = useContext(HrTicketDetailContext);
  const { ticketSelected } = useContext(AppContext);
  const [refTicketId, setRefTicketId] = useState<string | undefined>(undefined);
  const [businessTripTicketId, setBusinessTripTicketId] = useState<number | undefined>(undefined);
  const breakpoints = useBreakpoint();
  const { id } = useParams();
  const colspan = breakpoints.xxl ? 2 : 1;

  const advancePaymentTicketQuery = useQuery({
    queryKey: ['advancePaymentTicket', id],
    queryFn: () => getAdvancePaymentTicket(Number(id)),
    onSuccess: (data) => {
      setRefTicketId(data.data.refTicketId ?? undefined);
      setBusinessTripTicketId(data.data.businessTripTicketId ? Number(data.data.businessTripTicketId) : undefined);
    },
    enabled: !!id && !!ticketSelected
  });

  const businessTripTicketQuery = useQuery({
    queryKey: ['businessTripTicket', businessTripTicketId],
    queryFn: () => getBusinessTripTicket(businessTripTicketId as number),
    enabled: !!businessTripTicketId && !!id,
    staleTime: 60 * 1000
  });

  const refTicketQuery = useQuery({
    queryKey: ['ticket', refTicketId],
    queryFn: () => getTicket(refTicketId ?? ''),
    enabled: !!refTicketId,
    staleTime: 60 * 1000
  });

  useEffect(() => {
    if (advancePaymentTicketQuery.data?.data) {
      setAdvancePaymentTicket(advancePaymentTicketQuery.data);
      setLoading(advancePaymentTicketQuery.isFetching);
      setInvalidKey(JSON.stringify(advancePaymentTicketQuery.data?.data));
    }
  }, [
    advancePaymentTicketQuery.data,
    advancePaymentTicketQuery.isFetching,
    setAdvancePaymentTicket,
    setInvalidKey,
    setLoading
  ]);

  const advancePaymentTicketDetailItems = (
    advancePaymentTicketDetails: IAdvancePaymentTicketDetail[]
  ): CollapseProps['items'] => {
    return advancePaymentTicketDetails.map((advancePaymentTicketDetail) => ({
      key: advancePaymentTicketDetail.id,
      label: (
        <Descriptions
          className='advance-payment-ticket-item-header'
          layout='horizontal'
          labelStyle={{ width: '135px' }}
          column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
        >
          <Descriptions.Item label='Mã chi phí'>{advancePaymentTicketDetail.expenseType.code}</Descriptions.Item>
          <Descriptions.Item label='Tên chi phí'>{advancePaymentTicketDetail.expenseType.name}</Descriptions.Item>
          <Descriptions.Item label='	Mô tả chi phí' span={colspan}>
            {advancePaymentTicketDetail?.description || ''}
          </Descriptions.Item>
          <Descriptions.Item label='Số tiền'>
            {`${formatToVND(advancePaymentTicketDetail.unitPrice)} ${advancePaymentTicketDetail?.unitOfCurrency?.code}`}
          </Descriptions.Item>
          <Descriptions.Item label='Đơn vị tiền tệ'>
            {`${advancePaymentTicketDetail?.unitOfCurrency?.name} - ${advancePaymentTicketDetail?.unitOfCurrency?.code}`}
          </Descriptions.Item>
        </Descriptions>
      ),
      className: 'advance-payment-ticket-item'
    }));
  };

  const tabsItems = () => {
    const items = [
      {
        label: 'Phiếu tạm ứng',
        key: '1',
        children: (
          <Fragment>
            <Flex justify='start'>
              <Title level={5}>Thông tin phiếu</Title>
            </Flex>
            {advancePaymentTicketQuery.isFetching ? (
              <Skeleton />
            ) : (
              <Fragment>
                <Descriptions
                  layout='horizontal'
                  labelStyle={{ width: '135px' }}
                  column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
                >
                  <Descriptions.Item label='Mã phiếu'>
                    <Text strong>{advancePaymentTicketQuery.data?.data?.code}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={businessTripTicketId ? 'Số phiếu công tác' : 'Số ticket'}>
                    {(advancePaymentTicketQuery?.data?.data?.refTicketCode ||
                      advancePaymentTicketQuery?.data?.data?.businessTripTicket?.code) && (
                      <Button type='link' onClick={() => setActiveKey('2')}>
                        {advancePaymentTicketQuery?.data?.data?.refTicketCode
                          ? advancePaymentTicketQuery?.data?.data?.refTicketCode
                          : advancePaymentTicketQuery?.data?.data?.businessTripTicket?.code}
                      </Button>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label='Ngày tạo'>
                    <Text>{formatDate(advancePaymentTicketQuery.data?.data?.createdDate)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Trạng thái'>
                    <HRTicketStatusTag ticketStatus={advancePaymentTicketQuery.data?.data.ticketStatus} />
                  </Descriptions.Item>
                  <Descriptions.Item label='Người tạo'>
                    <Text>{advancePaymentTicketQuery.data?.data?.createdBy?.fullName}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Bộ phận'>
                    <Text>{advancePaymentTicketQuery.data?.data?.createdBy?.department?.name}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Người đề nghị'>
                    <Text>{advancePaymentTicketQuery.data?.data?.employee.fullName}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Bộ phận'>
                    <Text>{advancePaymentTicketQuery.data?.data?.employee?.department?.name}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Ngày đề nghị thanh toán' span={colspan}>
                    <Text>{formatDate(advancePaymentTicketQuery.data?.data?.requestDate)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Dự án'>
                    <Text>{advancePaymentTicketQuery.data?.data?.projectCode}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Dòng tiền'>
                    <Text>{advancePaymentTicketQuery.data?.data?.cashFlow}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Đợt'>
                    <Text>{advancePaymentTicketQuery.data?.data?.paymentBatch}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Tỷ lệ'>
                    <Text>{advancePaymentTicketQuery.data?.data?.paymentRate} %</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Phương thức thanh toán' span={colspan}>
                    <Text>{advancePaymentTicketQuery.data?.data?.paymentMethod?.name}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Nội dung yêu cầu tạm ứng' span={colspan}>
                    <Text strong italic>
                      {advancePaymentTicketQuery.data?.data?.note || ''}
                    </Text>
                  </Descriptions.Item>
                </Descriptions>
                <Divider style={{ marginTop: 16, marginBottom: 16 }} />
                <Flex justify='start'>
                  <Title level={5}>Danh sách tạm ứng</Title>
                </Flex>
                <Collapse
                  bordered={false}
                  items={advancePaymentTicketDetailItems(
                    advancePaymentTicketQuery.data?.data.advancePaymentTicketDetails || []
                  )}
                  expandIconPosition='end'
                  className='advance-payment-ticket-list'
                />
              </Fragment>
            )}
          </Fragment>
        )
      },
      {
        label: `Phiếu liên kết - ${businessTripTicketQuery.data?.data ? 'Công tác' : 'Ticket'}`,
        key: '2',
        children: (
          <Fragment>
            <Flex justify='start'>
              <Title level={5}>Thông tin phiếu</Title>
            </Flex>
            {refTicketQuery.data && (
              <Spin size='small' spinning={refTicketQuery.isFetching}>
                <Descriptions column={2} labelStyle={{ width: '130px' }} layout='horizontal'>
                  <Descriptions.Item label='Mã phiếu'>
                    <Text strong>{refTicketQuery?.data?.data?.ticketCode}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Trạng thái'>
                    <TicketStatusTag ticketStatus={refTicketQuery.data?.data?.ticketStatus} />
                  </Descriptions.Item>
                  <Descriptions.Item label='Danh mục' span={colspan}>
                    {refTicketQuery?.data?.data?.ticketCategory?.name}
                  </Descriptions.Item>
                  <Descriptions.Item label='Ngày tạo'>{formatDate(refTicketQuery?.data?.data?.date)}</Descriptions.Item>
                  <Descriptions.Item label='Người tạo'>
                    <Text>{refTicketQuery?.data?.data?.createdByObj?.name}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Người xử lý' span={colspan}>
                    <Text>{refTicketQuery?.data?.data?.handlerObj?.name || ''}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Giải pháp' span={colspan}>
                    <Text>
                      {Parser(refTicketQuery?.data?.data?.ticketSolutions?.find((x) => x.isApproval)?.solution || '')}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Nội dung yêu cầu' span={colspan}>
                    <Text> {Parser(refTicketQuery?.data?.data?.remark || '')}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </Spin>
            )}
            {businessTripTicketQuery.data && (
              <Spin size='small' spinning={businessTripTicketQuery.isFetching}>
                <Descriptions column={2} labelStyle={{ width: '110px' }} layout='horizontal'>
                  <Descriptions.Item label='Mã phiếu' span={colspan}>
                    <Text strong>{businessTripTicketQuery?.data?.data?.code}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Trạng thái' span={colspan}>
                    <HRTicketStatusTag
                      ticketStatus={businessTripTicketQuery.data?.data.ticketStatus}
                      erpKey={businessTripTicketQuery.data?.data.erpKey}
                      isConfirm={businessTripTicketQuery.data?.data.isConfirm}
                      hrConfirmedType={businessTripTicketQuery.data?.data.hrConfirmedType}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label='Ngày tạo'>
                    {formatDate(businessTripTicketQuery?.data?.data?.createdDate)}
                  </Descriptions.Item>
                  <Descriptions.Item label='Người tạo'>
                    <Text>{businessTripTicketQuery?.data?.data?.createdBy?.fullName}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Nơi đi'>
                    <Text>{Parser(businessTripTicketQuery.data?.data?.departureIdInString || '')}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Nơi đến'>
                    <Text>{Parser(businessTripTicketQuery.data?.data?.destinationIdInString || '')}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Phiếu liên kết'>
                    <Text strong>{businessTripTicketQuery.data?.data?.refTicketCode}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label='Chi tiết công việc'>
                    <Text strong>{Parser(businessTripTicketQuery.data?.data?.note || '')}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </Spin>
            )}
          </Fragment>
        )
      }
    ];

    if (!businessTripTicketQuery.data?.data && !refTicketQuery.data?.data) {
      items.splice(1, 1);
    }
    return items;
  };

  return (
    <div className='advance-payment-ticket-detail-wrapper'>
      <BtnHrHandle type='advancePaymentTicket' />

      <Divider style={{ marginTop: 16, marginBottom: 16 }} />

      <Tabs
        defaultActiveKey='1'
        type='card'
        size='middle'
        items={tabsItems()}
        activeKey={activeKey}
        onTabClick={(activeKey: string) => setActiveKey(activeKey as ActiveType)}
      />
    </div>
  );
}
